import { Component, Input, OnInit, NgZone, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../feature-module/core/services/modal.service';
import { RegisterModalComponent } from '../register-modal/register-modal.component';
import { DynamicModelComponent } from '../../../feature-module/core/common/dynamic-model/dynamic-model.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AuthService } from '../../core/service/auth.service';
import { environment } from '../../../../enviroments/environments';
import { ForgetPassordModalComponent } from '../forget-passord-modal/forget-passord-modal.component';
import { SocialAuthService, SocialUser, FacebookLoginProvider } from '@abacritt/angularx-social-login';

// Extend the google type on the window object to include 'accounts'
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (options: any) => void;
          renderButton: (element: HTMLElement | null, options: any) => void;
          prompt: (callback?: (notification: any) => void) => void; // Add prompt method
        };
      };
    };
  }
}

@Component({
  selector: 'app-login-modal',
  templateUrl: 'login-modal.component.html',
  styleUrls: ['./login-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
})

export class LoginModalComponent implements OnInit, AfterViewInit {

  @Input() message: string = '';
  password: boolean[] = [false, false]; // Add more as needed
  loginForm: FormGroup;
  errorString: string = '';
  socialUser: SocialUser | null = null;
  isLoggedin: boolean = false;

  constructor(
    private readonly modalService: ModalService,
    private readonly compRef: DynamicModelComponent,
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly socialAuthService: SocialAuthService,
    private readonly ngZone: NgZone
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      tenantId: [environment.tenantId, Validators.required],
    });
  }

  togglePassword(index: number): void {
    this.password[index] = !this.password[index];
  }

  ngOnInit() {
    this.socialAuthService.authState.subscribe((user) => {
      this.socialUser = user;
      this.isLoggedin = !!user;
    });
  }

  ngAfterViewInit() {
    // Initialize GIS and register the credential callback after the modal is in the DOM
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.initialize({
        client_id: '235145784159-5mj180f4o4a9sk5podufr533rjpb82e8.apps.googleusercontent.com',
        callback: (response: any) => {
          if (response && response.credential) {
            this.ngZone.run(() => {
              this.handleGoogleCredential(response.credential);
            });
          } else {
            this.ngZone.run(() => {
              this.errorString = 'Google login failed: No credential received.';
            });
          }
        }
      });
      // No renderButton call here for custom button
    }
  }

  handleGoogleCredential(credential: string) {
    // Send the credential (JWT) to your backend for verification and login
    // Example:
    this.authService.loginWithGoogle(credential).subscribe({
      next: (response) => {
        // handle login success (e.g., close modal)
        this.close('loggedIn');
      },
      error: (err) => {
        // handle login error (e.g., show error message)
        this.errorString = err.error?.message || 'Google login failed. Please try again.';
      }
    });
  }

  onCustomGoogleLogin(): void {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          this.ngZone.run(() => {
            this.errorString = 'Google login was cancelled or could not be displayed.';
          });
        }
      });
    } else {
      this.ngZone.run(() => {
        this.errorString = 'Google login is not available. Please try again later.';
      });
    }
  }

  close(result: string = '') {
    this.compRef.close(result);
  }

  signUp() {
    this.close();
    this.modalService.open(RegisterModalComponent, { size: 'md' }).subscribe();
  }

  login() {
    if (this.loginForm.invalid) return;
    this.authService.login(this.loginForm.value).subscribe({
      next: (response) => {
        // handle login success (e.g., close modal)
        this.close('loggedIn');
      },
      error: (err) => {
        // handle login error (e.g., show error message)
        console.error('Login failed:', err);
        this.errorString = err.error?.message || 'Login failed. Please try again.';
      }
    });
  }

  forgotPassword() {
    this.close();
    this.modalService.open(ForgetPassordModalComponent, { size: 'md' }).subscribe();
  }

  signInWithFacebook(): void {
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID).then(user => {
      this.close('loggedIn');
    }).catch(err => {
      this.errorString = 'Facebook login failed.';
    });
  }
}
